import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import { useOpportunities } from './useOpportunities';
import { useAccounts } from './useAccounts';
import { useContacts } from './useContacts';
import { useDocuments } from './useDocuments';
import { useRouter } from 'vue-router';
import { CustomEventNames } from '@apparatix/constants/CustomEventNames';
import type { AvailableViews } from '@apparatix/types';

type CrmPage = 'Leads' | 'Accounts' | 'Contacts' | 'Opportunities' | 'Documents' | 'Expirations' | 'Offer Management' | 'Activities';
export type CrmDetailTabName = 'overview' | 'relationships' | 'deals' | 'activity' | 'files';

// Lighting Configuration, Manage Measurements, and Manage Pricing are vue-routed screens with no
// real iframe for AppStore.refreshCurrentPage() to reload, so they refresh via a custom event
// their component listens for instead.
const ROOT_VIEW_RELOAD_EVENTS: Partial<Record<AvailableViews, string>> = {
  lightingConfiguration: CustomEventNames.lighting.reloadConfigHome,
  manageMeasurements: CustomEventNames.measurements.reloadHome,
  managePricing: CustomEventNames.pricing.reloadHome,
};

export const useCrm = () => {
  const opportunityStore = useOpportunityStore();
  const { loadOpportunities } = useOpportunities();
  const { loadAccountsHome } = useAccounts();
  const { loadContactsHome } = useContacts();
  const { loadDocumentsHome } = useDocuments();

  const router = useRouter();

  const reloadOpportunitiesHome = async () => {
    opportunityStore.protectMasterList = false;
    await loadOpportunities('Opportunity', 'high');
    opportunityStore.protectMasterList = true;
  };

  const reloadLeadsHome = async () => {
    opportunityStore.protectMasterList = false;
    await loadOpportunities('Lead', 'high');
    opportunityStore.protectMasterList = true;
  };

  const reloadActivityHome = () => {
    // Since activities home doesn't use a store, and has a complicated initialization, we just fire an event and let the page itself handle it
    const reloadEvent = new CustomEvent(CustomEventNames.crm.activities.reloadActivityHome, {
      bubbles: true,
      cancelable: true,
    });

    window.dispatchEvent(reloadEvent);
  };

  const reloadExpirationsHome = () => {
    const reloadEvent = new CustomEvent(CustomEventNames.crm.expirations.reloadExpirationsHome, {
      bubbles: true,
      cancelable: true,
    });

    window.dispatchEvent(reloadEvent);
  };

  const reloadOfferManagementHome = () => {
    window.dispatchEvent(new CustomEvent(CustomEventNames.crm.offerManagement.reloadOfferManagementHome, {
      bubbles: true,
      cancelable: true,
    }));
  };

  const handleSubpageItemClick = (newPage: string, currentPage: string) => {
    if (newPage.toLowerCase() !== currentPage.toLowerCase()) return;
    handleCrmPageRefresh(currentPage);
  };

  // Returns true when rootView maps to a reload event (and dispatches it), false otherwise, so
  // callers can tell whether the refresh was handled here or still needs another path (e.g. the
  // legacy iframe reload).
  const dispatchRootViewRefresh = (rootView?: AvailableViews): boolean => {
    const eventName = rootView ? ROOT_VIEW_RELOAD_EVENTS[rootView] : undefined;

    if (!eventName) {
      return false;
    }

    window.dispatchEvent(new CustomEvent(eventName, {
      bubbles: true,
      cancelable: true,
    }));

    return true;
  };

  const handleCrmPageRefresh = async (page: string) => {
    switch (page as CrmPage) {
      case 'Leads':
        await reloadLeadsHome();
        break;
      case 'Accounts':
        await loadAccountsHome(true);
        break;
      case 'Contacts':
        loadContactsHome(true);
        break;
      case 'Opportunities':
        await reloadOpportunitiesHome();
        break;
      case 'Documents':
        loadDocumentsHome(true);
        break;
      case 'Expirations':
        reloadExpirationsHome();
        break;
      case 'Offer Management':
        reloadOfferManagementHome();
        break;
      case 'Activities':
        reloadActivityHome();
        break;
      default:
        return;
    }
  };

  const handleDetailTabUpdate = (newTabValue: CrmDetailTabName, refUpdater: () => void) => {
    router.replace({ params: { activeTab: newTabValue }});
    refUpdater();
  };

  const getCrmDetailTabList = () => [
    'overview',
    'relationships',
    'deals',
    'activity',
    'files',
    'profile',
  ];

  // arguably contains superfluous check given parameter type hinting
  const isDetailTab = (value: string): boolean => typeof value === 'string' && getCrmDetailTabList().includes(value.toLowerCase());

  return {
    handleCrmPageRefresh,
    handleSubpageItemClick,
    dispatchRootViewRefresh,
    handleDetailTabUpdate,
    isDetailTab,
  };
};
